import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { isAuthenticated, getUser, setUser } from '@/utils/auth';
import api from '@/utils/api';

// 2026-05-31 — P0 billing fix.
// Statuses that mean "the user has not paid and may not enter the app".
// Internal/founder accounts (is_internal) ALWAYS bypass this gate.
const BLOCKING_STATUSES = new Set(['pending_payment', 'past_due', 'unpaid', 'canceled']);

// Round 69 (2026-02-13) — Stripe redirect-vs-webhook race.
// When Stripe redirects the browser back to ``/dashboard?session_id=
// cs_xxx&success=true``, the localStorage user is still on
// ``subscription_status='pending_payment'`` because the
// ``checkout.session.completed`` webhook may not have fired yet.
// ProtectedRoute used to read that stale state and bounce the user
// to ``/pricing?required=1``. This module now reconciles the
// checkout session SYNCHRONOUSLY against the backend on first load
// before letting the gate fire.

const userNeedsCheckout = () => {
  const user = getUser() || {};
  if (user.is_internal) return false;
  const status = (user.subscription_status || '').toLowerCase();
  return BLOCKING_STATUSES.has(status);
};

// Module-level cache so the verify call fires at most once per
// session_id, even across rapid re-mounts (StrictMode double-render
// in dev, race with sibling protected routes, etc.).
const _verifiedSessionIds = new Set();

const verifyCheckoutSession = async (sessionId) => {
  if (_verifiedSessionIds.has(sessionId)) return null;
  _verifiedSessionIds.add(sessionId);
  try {
    const { data } = await api.post(
      '/billing/verify-checkout-session', { session_id: sessionId },
    );
    if (data?.user) {
      setUser(data.user);
      return data.user;
    }
  } catch (err) {
    // 409 session_not_complete is the expected race-loss path —
    // fall back to a fresh /auth/me which the webhook may already
    // have stamped trialing on. Other errors propagate as auth
    // bounce.
    try {
      const { data } = await api.get('/auth/me');
      if (data) {
        setUser(data);
        return data;
      }
    } catch (_) {
      // Ignore — caller will bounce to /pricing if needed.
    }
  }
  return null;
};

export const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isCheckoutReturn =
    !!sessionId && searchParams.get('success') === 'true';

  // ``reconciling`` is true while we round-trip to the backend to
  // confirm Stripe's session and refresh localStorage. Render a
  // tiny inline loader so the user never sees the bounce-to-pricing
  // flash.
  const [reconciling, setReconciling] = useState(
    () => isCheckoutReturn && isAuthenticated() && userNeedsCheckout(),
  );
  const didRunRef = useRef(false);

  useEffect(() => {
    if (!isCheckoutReturn) return;
    if (didRunRef.current) return;
    didRunRef.current = true;
    if (!isAuthenticated()) return;
    (async () => {
      try {
        await verifyCheckoutSession(sessionId);
      } finally {
        // Round 69 — always unmount the loader. The previous
        // `!cancelled` guard interacted badly with React StrictMode's
        // double-mount cleanup and could pin the loader open on the
        // 409 race-loss fallback. React 18 no longer warns on
        // setState after unmount, so this is safe.
        setReconciling(false);
      }
    })();
  }, [isCheckoutReturn, sessionId]);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // While reconciling a fresh Stripe return, hold the render. This
  // is intentionally tiny and self-contained — no toast, no flash.
  if (reconciling) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300"
        data-testid="checkout-reconciling"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 rounded-full border-2 border-slate-700 border-t-indigo-400 animate-spin" />
          <p className="text-sm">Activating your subscription…</p>
        </div>
      </div>
    );
  }

  // Public users without a valid subscription are bounced to /pricing
  // so they can complete (or restart) Stripe Checkout. Don't loop on
  // the pricing route itself.
  if (userNeedsCheckout() && location.pathname !== '/pricing') {
    return <Navigate to="/pricing?required=1" replace />;
  }
  return children;
};

export const PublicRoute = ({ children }) => {
  if (isAuthenticated() && !userNeedsCheckout()) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};
